import { useId, useState, type FormEvent, type ReactNode } from 'react'

import type { CustomerRow, CustomerSearch } from '@/api/customers'
import { EmptyState, formatInteger } from '@/design'

import { CustomerTable } from './CustomerTable'
import type { SearchState } from './customerSearch'
import { failureMessage } from './errors'
import type { CustomerParams } from './params'

/** The API's bounds on what it searches for (README *`POST /analytics/customers/search`*). */
const EMAIL_LENGTH = { min: 3, max: 320 }
const ID_LENGTH = { min: 1, max: 128 }

export type SearchParse = { search: CustomerSearch } | { problem: string } | null

/**
 * What a typed term asks for: an address when it holds an `@`, else a website customer
 * id, which only a role that may reveal ids can search by. Null for nothing typed;
 * `problem` for a term the API would refuse, said here rather than sent.
 */
export function parseSearch(raw: string, canSearchById: boolean): SearchParse {
  const term = raw.trim()
  if (!term) return null
  if (term.includes('@')) {
    if (term.length < EMAIL_LENGTH.min || term.length > EMAIL_LENGTH.max) return { problem: 'That email address is too long or too short to search for.' }
    return { search: { email: term } }
  }
  if (!canSearchById) {
    return { problem: 'Enter an email address. Searching by website customer id needs the permission to reveal customer ids.' }
  }
  if (term.length > ID_LENGTH.max) return { problem: 'A website customer id is at most 128 characters.' }
  return { search: { externalCustomerId: term } }
}

/**
 * One box to find a customer: an email address, or (for a role that may reveal ids) a
 * full website customer id. What is typed stays in this box's own state and the page's
 * memory: the form has no action and its field no name, so nothing is ever put in a URL,
 * and the browser is asked not to remember it.
 */
export function SearchBox({
  canSearchById,
  searching,
  onSearch,
}: {
  canSearchById: boolean
  searching: boolean
  onSearch: (search: CustomerSearch) => void
}) {
  const [draft, setDraft] = useState('')
  const [problem, setProblem] = useState<string | null>(null)
  const hintId = useId()
  const problemId = useId()

  const submit = (event: FormEvent) => {
    event.preventDefault()
    const parsed = parseSearch(draft, canSearchById)
    if (parsed === null) return
    if ('problem' in parsed) {
      setProblem(parsed.problem)
      return
    }
    setProblem(null)
    onSearch(parsed.search)
  }

  return (
    <form role="search" aria-label="Find a customer" className="cus-search" onSubmit={submit} noValidate>
      <label className="ci-field">
        <span className="ci-field__label">{canSearchById ? 'Find by email or website customer id' : 'Find by email'}</span>
        <input
          type="text"
          inputMode={canSearchById ? 'text' : 'email'}
          autoComplete="off"
          spellCheck={false}
          value={draft}
          aria-describedby={problem ? `${hintId} ${problemId}` : hintId}
          aria-invalid={problem ? true : undefined}
          onChange={(event) => {
            setDraft(event.target.value)
            setProblem(null)
          }}
        />
      </label>
      <button type="submit" className="ci-button" disabled={searching || !draft.trim()}>
        {searching ? 'Searching…' : 'Search'}
      </button>
      <p id={hintId} className="list-muted list-hint cus-search__hint">
        Finds customers who allowed advertising cookies, by email address.
        {canSearchById ? ' A full website customer id finds any customer.' : ''} Every search is recorded in the audit
        log.
      </p>
      {problem ? (
        <p id={problemId} role="alert" className="list-error">
          {problem}
        </p>
      ) : null}
    </form>
  )
}

/** Why a search failed, honestly, and never repeating what was searched for. */
function searchError(error: unknown, byEmail: boolean): string {
  return failureMessage(
    error,
    {
      403: byEmail
        ? 'This search was refused: your role cannot read customer analytics here, or the website is suspended.'
        : 'This search was refused: searching by website customer id needs the permission to reveal customer ids.',
      422: 'That search could not be run. Check what was typed and try again.',
    },
    'The search could not be run just now. Try again in a moment.',
  )
}

/** The search's answer in the list's own table, with the way back to the list. */
export function SearchResults({
  state,
  params,
  identity,
  onClear,
}: {
  state: Exclude<SearchState, { status: 'idle' }>
  params: CustomerParams
  identity: (row: CustomerRow) => ReactNode
  onClear: () => void
}) {
  const byEmail = 'email' in state.search
  const found = state.status === 'done' ? state.result.latest.total : null
  return (
    <section className="cus-search-results" aria-label="Search results">
      <div className="list-footer">
        <p role="status" className="list-note">
          {state.status === 'loading'
            ? 'Searching…'
            : state.status === 'failed'
              ? 'The search did not run.'
              : `Search results: ${found === 1 ? '1 customer' : `${found ?? 0} customers`} found ${byEmail ? 'by email' : 'by website customer id'}.`}
        </p>
        <button type="button" className="ci-button" onClick={onClear}>
          Clear search
        </button>
      </div>
      {state.status === 'failed' ? (
        <p role="alert" className="list-error">
          {searchError(state.error, byEmail)}
        </p>
      ) : null}
      {state.status === 'done' && state.result.latest.total > state.result.rows.length ? (
        <p role="note" className="list-note">
          Showing the first {formatInteger(state.result.rows.length)} of {formatInteger(state.result.latest.total)} matches.
          Narrow the search or the filters to see the rest.
        </p>
      ) : null}
      {state.status === 'done' ? (
        <CustomerTable
          result={state.result}
          params={params}
          identity={identity}
          paging={null}
          empty={
            <EmptyState
              variant="no-results"
              compact
              title="No customer found"
              description={
                byEmail
                  ? 'Email search finds only customers who allowed advertising cookies, and only among the customers listed. Try All customers to look beyond this period.'
                  : 'No customer listed has that website id. Try All customers to look beyond this period.'
              }
              action={
                params.basis === 'all' ? undefined : (
                  <button type="button" className="ci-button" onClick={() => params.setBasis('all')}>
                    Search all customers
                  </button>
                )
              }
            />
          }
        />
      ) : null}
    </section>
  )
}
